import Box from "@mui/material/Box";
import InputRadioGroup from "common/components/Elements/Form/InputRadioGroup/InputRadioGroup";
import ModalActions from "common/components/Elements/Modal/ModalActions/ModalActions";
import SimpleModal from "common/components/Elements/Modal/SimpleModal/SimpleModal";
import { buildFileName, downloadTextFile } from "common/utilities/browser/downloadFile/downloadFile";
import {
  enqueueErrorNotification,
  enqueueWarningNotification,
} from "core/redux/actions/notification/notificationActions";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import { getStore } from "core/redux/store/configureStore.ts";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import type { ValueLabelOption } from "core/utilities/core.types";
import I18n from "i18n/en-US";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceType,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import {
  selectedAlignedSequences,
  sequenceFeaturesSelectionAsFastaString,
} from "sequenceAnalyser/utilities/selectionActions/selectionActions";
import type { FeatureDefinitions } from "sequenceAnalyser/utilities/sequenceAnalyser.types";

import { getSequencesForDownload } from "../../../../api/fetch.ts";

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  sessionId: string;
  selection: SequenceSelection;
  rawSequences: Sequence[];
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  sequenceType: SequenceType;
  featureDefinitions: FeatureDefinitions;
}

function FileExportModal({
  open,
  onOpen,
  sessionId,
  rawSequences,
  sequences,
  alignedSequences,
  selection,
  sequenceType,
  featureDefinitions,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();

  const labelComposition = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings.labelComposition);

  // Scope
  const scopes: ValueLabelOption[] = [
    { value: "all", label: I18n.SequenceAnalyser.FileExportModal.all },
    {
      value: "selection",
      label: I18n.SequenceAnalyser.FileExportModal.selection(
        selection.ids.length,
        selection.positionStart === undefined ? undefined : selection.positionStart + 1,
        selection.positionEnd === undefined ? undefined : selection.positionEnd + 1,
      ),
      disabled: selection.ids.length === 0,
    },
  ];

  const [scope, setScope] = React.useState(selection.ids.length ? "selection" : "all");

  // Content
  const contents: ValueLabelOption[] = [
    { value: "sequences", label: I18n.SequenceAnalyser.FileExportModal.sequences },
    { value: "features", label: I18n.SequenceAnalyser.FileExportModal.features },
    { value: "featuresRaw", label: I18n.SequenceAnalyser.FileExportModal.featuresRaw },
  ];

  const [content, setContent] = React.useState("sequences");

  // File types
  const fileTypes: ValueLabelOption[] = [
    { value: "fasta", label: "FASTA" },
    { value: "msf", label: "MSF", disabled: content !== "sequences" },
  ];

  const [fileType, setFileType] = useState<string>(fileTypes[0].value);

  // Download process
  const [loading, setLoading] = useState(false);

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.FileExportModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={
        <Box mt={1}>
          <InputRadioGroup
            label={I18n.SequenceAnalyser.FileExportModal.scope}
            options={scopes}
            onChange={(value) => setScope(value)}
            value={scope}
            horizontal
            modalLabel
          />

          <InputRadioGroup
            label={I18n.SequenceAnalyser.FileExportModal.content}
            options={contents}
            onChange={(value) => {
              setContent(value);
              if (value !== "sequences") setFileType("fasta");
            }}
            value={content}
            horizontal
            modalLabel
          />

          <InputRadioGroup
            label={I18n.SequenceAnalyser.FileExportModal.fileType}
            options={fileTypes}
            onChange={(value) => setFileType(value)}
            value={fileType}
            horizontal
            modalLabel
          />

          <ModalActions
            applyText={I18n.SequenceAnalyser.FileExportModal.apply}
            persisting={loading}
            onApply={async () => {
              setLoading(true);

              try {
                if (content !== "sequences") {
                  const fileContent = sequenceFeaturesSelectionAsFastaString(
                    content === "features" ? "aligned" : "raw",
                    rawSequences,
                    sequences,
                    alignedSequences,
                    scope === "all" ? { ids: [] } : selection,
                    featureDefinitions,
                    labelComposition,
                  );

                  if (fileContent.length === 0) {
                    dispatch(enqueueWarningNotification(I18n.SequenceAnalyser.TableActions.FileExportModal.noFeatures));
                  } else {
                    const filename = buildFileName(
                      [sequenceType === "protein" ? "ProteinSequences" : "NucleotideSequences", "features"].join("_"),
                      "fasta",
                    );

                    downloadTextFile(filename, fileContent);
                  }
                } else {
                  const filename = buildFileName(
                    [sequenceType === "protein" ? "ProteinSequences" : "NucleotideSequences", "features"].join("_"),
                    fileType,
                  );

                  const fileContent = await getSequencesForDownload({
                    seqAnalSessionId: sessionId,
                    saveRequest: {
                      format: fileType,
                      entitySequences: sequences?.map(
                        (s) =>
                          ({
                            id: s.id,
                            name: s.name,
                            qualifiedId: s.qualifiedId,
                            sequenceType: s.sequenceType,
                            residues: s.residues,
                            directStrand: s.directStrand,
                          }) as Sequence,
                      ),
                      alignedSequences:
                        selection.ids.length === 0
                          ? alignedSequences
                          : selectedAlignedSequences(alignedSequences, selection),
                      selection:
                        selection.ids.length > 0 &&
                        selection.positionStart !== undefined &&
                        selection.positionEnd !== undefined
                          ? {
                              positionStart: selection.positionStart,
                              positionEnd: selection.positionEnd,
                            }
                          : undefined,
                    },
                  });

                  if (fileContent) {
                    downloadTextFile(filename, fileContent);
                  }
                }
              } catch (error) {
                getStore().dispatch(enqueueErrorNotification(error instanceof Error ? error.message : String(error)));
              } finally {
                setLoading(false);
                onOpen(false);
              }
            }}
          />
        </Box>
      }
    />
  );
}

export default FileExportModal;
